import { filter, get, isEmpty, keys, reduce } from "lodash";
import { stringifyUrl } from "query-string";
import React, { useCallback, useMemo } from "react";
import { matchPath, useHistory, useLocation } from "react-router-dom";
import CenteredLoading from "../../../../components/CenteredLoading";
import DisplayLabels from "../../../../components/labels/DisplayLabels";
import FilterLabels from "../../../../components/labels/FilterLabels";
import VirtualScroll from "../../../../components/VirtualScroll";
import { useFilteredList } from "../../../../context/filteredList";
import { getSmartLabelFromKey } from "../../../../context/filteredList/FilteredListProvider";
import { useHotspotMakers } from "../../../../context/hotspotMakers";
import {
  hotspotMakerMetadata,
  HotspotVendor
} from "../../../../context/hotspotMakers/HotspotMakersProvider";
import { useLabels } from "../../../../context/labels";
import { useLoading } from "../../../../context/loading";
import { LoadingKey } from "../../../../context/loading/LoadingProvider";
import { useViewport } from "../../../../context/viewport";
import classNames from "../../../../utils/classNames";
import { avatarsApi, breakpoints, Entity } from "../../../../utils/constants";
import { locale } from "../../../../utils/dates";
import { HotspotMaker } from "../../../../utils/types";
import useFilteredLabels, {
  getFilteredLabels,
  LabelFilterKey
} from "../../../../utils/useFilteredLabels";
import useSideMenu from "../useSideMenu";

interface Props {}

const HotspotMakersList: React.FC<Props> = () => {
  const location = useLocation();
  const history = useHistory();
  const toggleSideMenu = useSideMenu((state) => state.toggleSideMenu);
  const { getIsReady } = useLoading();
  const { width } = useViewport();
  const isMobile = useMemo(() => width < breakpoints.md, [width]);
  const { getHotspotMakerName, featuredHotspotVendors } = useHotspotMakers();
  const { filteredList, smartLabelFilters } = useFilteredList();
  const { labels } = useLabels();
  const filteredLabels = useFilteredLabels((state) => state.filteredLabels);

  const filteredHotspotMakerLabels = useMemo(
    () =>
      getFilteredLabels(
        filteredLabels[LabelFilterKey.mapHotspotMakers],
        labels,
        smartLabelFilters[Entity.hotspotMaker],
        Entity.hotspotMaker
      ),
    [filteredLabels, labels, smartLabelFilters]
  );

  const filteredHotspots = useMemo(
    () => filteredList[Entity.hotspot],
    [filteredList]
  );

  const filteredHotspotMakers = useMemo(
    () => filteredList[Entity.hotspotMaker],
    [filteredList]
  );

  const detailsPageMatch = matchPath<{
    id?: string;
    section?: string;
    subsection?: string;
  }>(location.pathname, {
    path: [
      "/hotspots/makers/:id/:section/:subsection",
      "/hotspots/makers/:id/:section",
      "/hotspots/:id/:section/:subsection",
      "/hotspots/:id/:section"
    ]
  });

  const toggleHotspotMaker = useCallback(
    (hotspotMaker: HotspotMaker) => {
      if (isMobile) toggleSideMenu();

      const {
        id,
        section
      }: {
        id?: string;
        section?: string;
      } = detailsPageMatch?.params || {};

      if (!hotspotMaker || hotspotMaker.maker_id === id) {
        history.push("/hotspots/makers");
      } else {
        let sectionPath = "statistics";

        if (section) sectionPath = section;

        history.push(
          `/hotspots/makers/${hotspotMaker.maker_id}/${sectionPath}`
        );
      }
    },
    [detailsPageMatch?.params, history, isMobile, toggleSideMenu]
  );

  const renderHotspotMaker = useCallback(
    (index: number) => {
      const hotspotMaker = filteredHotspotMakers[index];

      return (
        <div
          key={hotspotMaker.maker_id}
          className={classNames(
            "relative px-6 py-5 flex items-center space-x-3 cursor-pointer hover:ring-2 ring-inset ring-hotspotty-400 border-b border-gray-200 dark:border-gray-500 dark:ring-white",
            detailsPageMatch?.params?.id === hotspotMaker.maker_id && "ring-2"
          )}
          onClick={() => toggleHotspotMaker(hotspotMaker)}
        >
          <div className="flex-shrink-0 flex justify-center items-center w-14">
            <img
              className="h-14 w-14 rounded-full"
              src={
                get(hotspotMakerMetadata, [hotspotMaker.maker_id, "logoUrl"]) ||
                stringifyUrl({
                  url: `${avatarsApi}/initials/${getHotspotMakerName(
                    hotspotMaker.maker_id
                  )}.svg`,
                  query: { w: 40, h: 40 }
                })
              }
              alt="Maker logo"
            />
          </div>
          <div className="flex-1 flex flex-col min-w-0 focus:outline-none">
            <span className="absolute inset-0" aria-hidden="true" />
            <div className="text-sm font-bold text-gray-900 dark:text-white truncate mb-1 flex items-center">
              {get(
                hotspotMakerMetadata,
                [hotspotMaker.maker_id, "labels"],
                []
              ).includes("Hotspotty Partner") && (
                <img
                  src="/favicon-32x32.png"
                  width="16"
                  height="16"
                  alt="Hotspotty logo"
                  className="inline-flex mr-1"
                />
              )}
              <span>{getHotspotMakerName(hotspotMaker.maker_id)}</span>
            </div>

            {hotspotMaker.installed_hotspots.total > 0 && (
              <span className="text-xs truncate text-gray-500 dark:text-gray-400">
                <b>{formatNumber(hotspotMaker.installed_hotspots.total)}</b>{" "}
                activated
              </span>
            )}

            {hotspotMaker.installed_hotspots.total >
              hotspotMaker.installed_hotspots.online && (
              <span className="text-xs truncate text-gray-500 dark:text-gray-400">
                <b>
                  {(
                    ((hotspotMaker.installed_hotspots.total -
                      hotspotMaker.installed_hotspots.online) /
                      hotspotMaker.installed_hotspots.total) *
                    100
                  ).toFixed(0)}
                </b>
                % inactive
              </span>
            )}

            <DisplayLabels
              linkedUuid={hotspotMaker.maker_id}
              labels={reduce(
                smartLabelFilters[Entity.hotspotMaker],
                (
                  acc: string[],
                  filterFunction: (hotspotMaker: HotspotMaker) => boolean,
                  filterKey: string
                ) => {
                  if (filterFunction(hotspotMaker)) {
                    acc.push(filterKey);
                  }
                  return acc;
                },
                []
              ).map((labelUuid) =>
                getSmartLabelFromKey(labelUuid, Entity.hotspotMaker)
              )}
              size="small"
            />
          </div>
          <div className="text-sm text-white flex flex-col items-center">
            <div className="font-bold">
              {formatNumber(
                filter(filteredHotspots, { payer: hotspotMaker.maker_id })
                  .length
              )}
            </div>
            <div className="text-xs text-gray-500 dark:text-white">nearby</div>
          </div>
        </div>
      );
    },
    [
      detailsPageMatch?.params?.id,
      filteredHotspotMakers,
      filteredHotspots,
      getHotspotMakerName,
      smartLabelFilters,
      toggleHotspotMaker
    ]
  );

  const isLoadingHotspotMakers = useMemo(
    () => !getIsReady([LoadingKey.hotspotMakers]),
    [getIsReady]
  );

  const renderContent = useMemo(() => {
    if (isLoadingHotspotMakers) {
      return <CenteredLoading className="h-full" />;
    }

    if (isEmpty(filteredHotspotMakers)) {
      return (
        <>
          <div className="z-10 sticky top-0 border-b border-t border-gray-200 bg-gray-50 text-gray-500 px-6 py-1 flex items-center justify-between dark:border-gray-500 dark:bg-gray-800 dark:text-gray-300">
            <div className="text-sm font-medium">0 makers</div>
          </div>
          <div className="px-6 pt-6 pb-4">
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
              No hotspot makers to show.
            </p>
          </div>
        </>
      );
    }

    return (
      <>
        <div className="z-10 sticky top-0 border-t border-b border-gray-200 bg-gray-50 text-gray-500 px-6 py-1 flex items-center justify-between dark:border-gray-500 dark:bg-gray-800 dark:text-gray-300">
          <div className="text-sm font-medium">
            {filteredHotspotMakers.length} hotspot maker
            {filteredHotspotMakers.length !== 1 && "s"}
          </div>
          <div className="text-xs"># maker hotspots</div>
        </div>

        <VirtualScroll
          className="overflow-y-auto"
          minItemHeight={80}
          totalLength={filteredHotspotMakers.length}
          renderItem={renderHotspotMaker}
        />
      </>
    );
  }, [filteredHotspotMakers, isLoadingHotspotMakers, renderHotspotMaker]);

  return (
    <>
      <div className="px-4 pb-2">
        <FilterLabels
          entity={Entity.hotspotMaker}
          filteredLabels={filteredHotspotMakerLabels}
          smartLabels={keys(smartLabelFilters[Entity.hotspotMaker]).map((key) =>
            getSmartLabelFromKey(key, Entity.hotspotMaker)
          )}
          filterKey={LabelFilterKey.mapHotspotMakers}
        />
      </div>

      {!isEmpty(featuredHotspotVendors) && (
        <>
          <div className="border-t border-b border-gray-200 bg-gray-50 text-gray-500 px-6 py-1 flex items-center justify-between dark:border-gray-500 dark:bg-gray-800 dark:text-gray-300">
            <div className="text-sm font-medium">Featured vendors</div>
          </div>
          <ul className="divide-y divide-gray-200 dark:divide-gray-500">
            {featuredHotspotVendors.map(renderFeaturedHotspotVendor)}
          </ul>
        </>
      )}

      {renderContent}
    </>
  );
};

export default HotspotMakersList;

//
// Utils
//

const formatNumber = (value: number) => Intl.NumberFormat(locale).format(value);

const renderFeaturedHotspotVendor = ({
  key,
  name,
  subtitle,
  purchaseLink
}: HotspotVendor) => (
  <li key={key}>
    <div className="relative px-6 py-5 flex items-center space-x-3">
      <div className="flex-1 truncate">
        <div className="text-md font-semibold text-gray-900 dark:text-white">
          {name}
        </div>
        {subtitle && (
          <div className="text-sm text-gray-800 dark:text-gray-200">
            {subtitle}
          </div>
        )}
      </div>

      <button
        onClick={() => {
          window.open(purchaseLink);
        }}
        type="button"
        className="px-2.5 py-1.5 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-hotspotty-400 hover:bg-hotspotty-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-400 disabled:opacity-50 disabled:cursor-default"
      >
        Shop now
      </button>
    </div>
  </li>
);
